import { createSlice, PayloadAction } from "@reduxjs/toolkit";

/**
 * Dashboard settings interface
 * Maps dashboard widget keys to their visibility state
 */
export interface DashboardSettings {
  [key: string]: boolean;
}

/**
 * User preferences state interface
 * Manages user-specific preferences and settings
 */
export interface UserPreferencesState {
  /**
   * Instance timezone preference
   * Format: IANA timezone string (e.g., 'America/New_York')
   */
  instanceTimezone: string | null;

  /**
   * Per-user DISPLAY timezone (user_preferences.timezone). null = floor to Pacific.
   * Used by the display seam's resolveDisplayTz. Populated from GET /user-preferences.
   */
  userTimezone: string | null;

  /**
   * Display tz MODE (user_preferences.timezone_mode). 'personal' = always the user's tz;
   * 'community' = the ACTIVE (top-bar-selected) community's tz, falling back to personal for
   * "All communities". The zone-DECIDER; independent of the community data filter. Read by the
   * community-tz gate (useCommunityTz) and TzMismatchBanner. Populated from GET /user-preferences.
   */
  userTimezoneMode: "personal" | "community";

  /**
   * Dashboard widget visibility settings
   * Maps widget identifiers to their visibility state
   */
  dashboardSettings: DashboardSettings;

  /**
   * Dashboard widget order
   * Array of widget keys determining display order
   */
  dashboardWidgetOrder: string[];

  /**
   * Last visited route for session restoration
   */
  lastVisitedRoute: string | null;
}

const initialState: UserPreferencesState = {
  instanceTimezone: null,
  userTimezone: null,
  userTimezoneMode: "personal",
  dashboardSettings: {},
  dashboardWidgetOrder: [],
  lastVisitedRoute: null,
};

/**
 * User Preferences slice for managing user-specific settings
 *
 * This slice handles user preferences that should persist across sessions,
 * including timezone settings, dashboard widget visibility, and navigation state.
 *
 * @example
 * ```typescript
 * // Set timezone
 * dispatch(setInstanceTimezone('America/New_York'));
 *
 * // Update dashboard settings
 * dispatch(setDashboardSettings({ widget1: true, widget2: false }));
 *
 * // Toggle individual widget
 * dispatch(toggleDashboardWidget('widget1'));
 *
 * // Set last visited route
 * dispatch(setLastVisitedRoute('/dashboard'));
 *
 * // Reset all preferences
 * dispatch(resetUserPreferences());
 * ```
 */
const userPreferencesSlice = createSlice({
  name: "userPreferences",
  initialState,
  reducers: {
    /**
     * Set the instance timezone preference
     * @param state - Current user preferences state
     * @param action - Payload containing the IANA timezone string
     */
    setInstanceTimezone: (state, action: PayloadAction<string>) => {
      state.instanceTimezone = action.payload;
    },

    /**
     * Populate the display-tz resolution inputs (from GET /prefs/community), so the
     * display seam's useDisplayTz can resolve without each site re-fetching.
     */
    setDisplayTzPrefs: (
      state,
      action: PayloadAction<{
        userTimezone: string | null;
        userTimezoneMode: "personal" | "community";
        instanceTimezone: string | null;
      }>,
    ) => {
      state.userTimezone = action.payload.userTimezone;
      state.userTimezoneMode = action.payload.userTimezoneMode;
      state.instanceTimezone = action.payload.instanceTimezone;
    },

    /**
     * Set all dashboard widget visibility settings
     * @param state - Current user preferences state
     * @param action - Payload containing dashboard settings object
     */
    setDashboardSettings: (state, action: PayloadAction<DashboardSettings>) => {
      state.dashboardSettings = action.payload;
    },

    /**
     * Toggle visibility of a specific dashboard widget
     * @param state - Current user preferences state
     * @param action - Payload containing the widget key to toggle
     */
    toggleDashboardWidget: (state, action: PayloadAction<string>) => {
      const widgetKey = action.payload;
      state.dashboardSettings[widgetKey] = !state.dashboardSettings[widgetKey];
    },

    /**
     * Set dashboard widget order
     * @param state - Current user preferences state
     * @param action - Payload containing array of widget keys in desired order
     */
    setDashboardWidgetOrder: (state, action: PayloadAction<string[]>) => {
      state.dashboardWidgetOrder = action.payload;
    },

    /**
     * Set the last visited route for session restoration
     * @param state - Current user preferences state
     * @param action - Payload containing the route path
     */
    setLastVisitedRoute: (state, action: PayloadAction<string>) => {
      state.lastVisitedRoute = action.payload;
    },

    /**
     * Reset all user preferences to initial state
     * @param state - Current user preferences state
     */
    resetUserPreferences: (state) => {
      state.instanceTimezone = null;
      state.userTimezone = null;
      state.userTimezoneMode = "personal";
      state.dashboardSettings = {};
      state.dashboardWidgetOrder = [];
      state.lastVisitedRoute = null;
    },
  },
});

export const {
  setInstanceTimezone,
  setDisplayTzPrefs,
  setDashboardSettings,
  setDashboardWidgetOrder,
  toggleDashboardWidget,
  setLastVisitedRoute,
  resetUserPreferences,
} = userPreferencesSlice.actions;

export default userPreferencesSlice.reducer;
